import { useRef, useEffect } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { useMaterialStore } from '../stores/useMaterialStore'
import { useDevModeStore } from '../stores/useDevModeStore'
import { useEnergyFlowStore } from '../stores/useEnergyFlowStore'
import { useTelemetryStore } from '../stores/useTelemetryStore'

const MATERIAL_NAME = 'Wires'

// Pre-allocated Color objects (module-level, avoids per-frame GC)
const _colorACharge = new THREE.Color()
const _colorBCharge = new THREE.Color()
const _colorADischarge = new THREE.Color()
const _colorBDischarge = new THREE.Color()
const _colorAIdle = new THREE.Color()
const _colorBIdle = new THREE.Color()
const _colorA = new THREE.Color()
const _colorB = new THREE.Color()

// Battery SOC column keys for averaging
const SOC_KEYS = Array.from({ length: 12 }, (_, i) => `battery_${i + 1}_soc`)
const SOC_LOOKBACK = 0.005 // ~7 minutes of data for direction detection
const SOC_THRESHOLD = 0.1 // minimum SOC delta to count as charging/discharging

function getAvgSOC(row: Record<string, number | string>): number {
  let sum = 0
  let count = 0
  for (const key of SOC_KEYS) {
    const val = row[key]
    if (typeof val === 'number') {
      sum += val
      count++
    }
  }
  return count > 0 ? sum / count : 0
}

// ---------------------------------------------------------------------------
// Inline GLSL shaders
// ---------------------------------------------------------------------------

const vertexShader = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`

const fragmentShader = /* glsl */ `
uniform vec3 uColorA;
uniform vec3 uColorB;
uniform float uIntensity;
uniform float uOpacity;
uniform float uBaseGlow;
uniform float uPulseAmplitude;
uniform float uPulseCount;
uniform float uEdgeSharpness;
uniform float uTailFalloff;
uniform float uScrollOffset;
uniform float uMagnitude;
uniform float uCoreWidth;

varying vec2 vUv;

void main() {
  // Traveling coordinate along wire (V axis)
  float coord = fract(vUv.y * uPulseCount - uScrollOffset);

  // Pulse shape: sharp leading edge + exponential tail
  float edge = smoothstep(0.0, 1.0 / uEdgeSharpness, coord);
  float tail = exp(-uTailFalloff * coord);
  float pulse = edge * tail;

  // Core highlight: brightest at the very front of the pulse
  float core = exp(-uTailFalloff * 3.0 * coord);

  // Blend between colorA (body) and colorB (hot core)
  vec3 pulseColor = mix(uColorA, uColorB, core * uCoreWidth);

  // Final color: base glow + pulse contribution scaled by magnitude
  float activePulse = pulse * uPulseAmplitude * uMagnitude;
  vec3 color = uColorA * uBaseGlow + pulseColor * activePulse;

  // Apply overall emission intensity
  color *= uIntensity;

  gl_FragColor = vec4(color, uOpacity);
}
`

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Animates the "Wires" material with a custom unlit ShaderMaterial.
 * Uses procedural traveling pulses with sharp leading edge and exponential
 * decay tail, color-coded by charge/discharge/idle state from telemetry.
 *
 * Must be called inside a component rendered within the R3F <Canvas>.
 */
export function useEnergyFlowAnimation() {
  const { scene } = useThree()

  const shaderMaterialRef = useRef<THREE.ShaderMaterial | null>(null)
  const meshesRef = useRef<THREE.Mesh[]>([])
  const originalMaterialRef = useRef<THREE.MeshStandardMaterial | null>(null)
  const assignedRef = useRef(false)
  const wasPlayingRef = useRef(false)
  const scrollOffsetRef = useRef(0)
  const smoothDirRef = useRef(0) // smoothed direction: -1 discharge, 0 idle, +1 charge

  // Subscribe to transparent for reactive effect
  const transparent = useEnergyFlowStore((s) => s.transparent)

  // Initialization: create ShaderMaterial
  useEffect(() => {
    const store = useEnergyFlowStore.getState()

    const material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        uColorA: { value: new THREE.Color(store.colorAIdle) },
        uColorB: { value: new THREE.Color(store.colorBIdle) },
        uIntensity: { value: store.intensity },
        uOpacity: { value: store.opacity },
        uBaseGlow: { value: store.baseGlow },
        uPulseAmplitude: { value: store.pulseAmplitude },
        uPulseCount: { value: store.pulseCount },
        uEdgeSharpness: { value: store.edgeSharpness },
        uTailFalloff: { value: store.tailFalloff },
        uScrollOffset: { value: 0 },
        uMagnitude: { value: 0 },
        uCoreWidth: { value: store.coreWidth },
      },
      transparent: store.transparent,
      side: THREE.DoubleSide,
      toneMapped: false,
    })

    shaderMaterialRef.current = material

    // Cleanup
    return () => {
      // Restore original materials on meshes
      if (originalMaterialRef.current) {
        for (const mesh of meshesRef.current) {
          mesh.material = originalMaterialRef.current
        }
      }
      meshesRef.current = []
      assignedRef.current = false

      material.dispose()
    }
  }, [])

  // Transparency update effect
  useEffect(() => {
    if (shaderMaterialRef.current) {
      shaderMaterialRef.current.transparent = transparent
      shaderMaterialRef.current.depthWrite = !transparent
      shaderMaterialRef.current.needsUpdate = true
    }
  }, [transparent])

  // Per-frame updates
  useFrame((_, delta) => {
    const dt = Math.min(delta, 0.1)
    const mat = shaderMaterialRef.current
    if (!mat) return

    // Lazy mesh assignment: wait for GLB to load, then swap materials
    if (!assignedRef.current) {
      const wireMat = useMaterialStore.getState().getThreeMaterial(MATERIAL_NAME)
      if (wireMat) {
        scene.traverse((child) => {
          if (child instanceof THREE.Mesh && child.material === wireMat) {
            meshesRef.current.push(child)
            child.material = mat
          }
        })
        if (meshesRef.current.length > 0) {
          assignedRef.current = true
          originalMaterialRef.current = wireMat
        }
      }
    }

    const uniforms = mat.uniforms

    // Idle guard
    const { dayNightPlaying, dayNightProgress } = useDevModeStore.getState()

    if (!dayNightPlaying) {
      wasPlayingRef.current = false
      const store = useEnergyFlowStore.getState()
      _colorAIdle.set(store.colorAIdle)
      _colorBIdle.set(store.colorBIdle)
      uniforms.uColorA.value.copy(_colorAIdle)
      uniforms.uColorB.value.copy(_colorBIdle)
      uniforms.uMagnitude.value = 0
      uniforms.uIntensity.value = store.intensity
      uniforms.uBaseGlow.value = store.baseGlow
      return
    }

    wasPlayingRef.current = true

    const store = useEnergyFlowStore.getState()
    const telemetry = useTelemetryStore.getState()

    // Derive direction from actual battery SOC data
    const currentRow = telemetry.getRowAtProgress(dayNightProgress)
    const prevRow = telemetry.getRowAtProgress(Math.max(0, dayNightProgress - SOC_LOOKBACK))

    let targetDir = 0
    if (currentRow && prevRow) {
      const socNow = getAvgSOC(currentRow)
      const socPrev = getAvgSOC(prevRow)
      const delta = socNow - socPrev
      if (delta > SOC_THRESHOLD)
        targetDir = 1 // charging
      else if (delta < -SOC_THRESHOLD) targetDir = -1 // discharging
    }

    // Smooth toward target direction to avoid color snapping
    smoothDirRef.current +=
      (targetDir - smoothDirRef.current) * Math.min(1, dt * store.colorLerpSpeed)
    const smoothDir = smoothDirRef.current
    const magnitude = Math.abs(smoothDir)

    // Scroll offset (traveling pulse position)
    const speed = store.minScrollSpeed + (store.scrollSpeed - store.minScrollSpeed) * magnitude
    const direction = smoothDir >= 0 ? -1 : 1
    scrollOffsetRef.current += dt * speed * direction
    scrollOffsetRef.current %= 1000 // prevent float overflow

    // 3-state color interpolation: charge / idle / discharge
    _colorACharge.set(store.colorACharge)
    _colorBCharge.set(store.colorBCharge)
    _colorADischarge.set(store.colorADischarge)
    _colorBDischarge.set(store.colorBDischarge)
    _colorAIdle.set(store.colorAIdle)
    _colorBIdle.set(store.colorBIdle)

    if (smoothDir > 0) {
      // Idle → Charge blend (smoothDir 0→1)
      _colorA.lerpColors(_colorAIdle, _colorACharge, Math.min(1, smoothDir))
      _colorB.lerpColors(_colorBIdle, _colorBCharge, Math.min(1, smoothDir))
    } else {
      // Idle → Discharge blend (smoothDir 0→-1)
      const t = Math.min(1, -smoothDir)
      _colorA.lerpColors(_colorAIdle, _colorADischarge, t)
      _colorB.lerpColors(_colorBIdle, _colorBDischarge, t)
    }

    // Set all uniforms
    uniforms.uColorA.value.copy(_colorA)
    uniforms.uColorB.value.copy(_colorB)
    uniforms.uIntensity.value = store.intensity
    uniforms.uOpacity.value = store.opacity
    uniforms.uBaseGlow.value = store.baseGlow
    uniforms.uPulseAmplitude.value = store.pulseAmplitude
    uniforms.uPulseCount.value = store.pulseCount
    uniforms.uEdgeSharpness.value = store.edgeSharpness
    uniforms.uTailFalloff.value = store.tailFalloff
    uniforms.uScrollOffset.value = scrollOffsetRef.current
    uniforms.uMagnitude.value = magnitude
    uniforms.uCoreWidth.value = store.coreWidth
  })
}
